import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { apiGet } from '../utils/api'
import { useTenant } from '../tenant/TenantContext'

type Ctx = {
  perms: string[]
  has: (p: string) => boolean
  loading: boolean
}

const PermsCtx = createContext<Ctx | null>(null)
const DefaultPerms: Ctx = { perms: [], has: () => true, loading: true }

export function usePerms() {
  try {
    const ctx = useContext(PermsCtx)
    return ctx ?? DefaultPerms
  } catch {
    return DefaultPerms
  }
}

export function PermsProvider({ children }: { children: React.ReactNode }) {
  const { current } = useTenant()
  const tenantId = current?.id
  const [perms, setPerms] = useState<string[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        if (tenantId) {
          const res = await apiGet(`/rbac/me/permissions?tenant_id=${tenantId}`)
          if (!cancelled) setPerms(res.permissions || [])
        } else {
          if (!cancelled) setPerms([])
        }
      } catch {
        if (!cancelled) setPerms([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [tenantId])

  const has = useMemo(() => (p: string) => perms.includes('*') || perms.includes(p), [perms])

  return (
    <PermsCtx.Provider value={{ perms, has, loading }}>
      {children}
    </PermsCtx.Provider>
  )
}
